import type {ModuleSpecField} from '../designer/specTypes';
import {templateDate,type TemplateDateStyle} from '@/uptk/core/components/moduleEntity/template/templateValue';

/**
 * QUIZ-NATIVE3 GAP-Q13 (générique) : une valeur d'exemple d'une capture est appariée aux données de la simulation chargée
 * (Relier mesuré) plutôt qu'à une autre capture. La ligne d'exemple est d'abord identifiée sans ambiguïté (par exemple la
 * question dont l'énoncé est celui de la capture), puis chaque segment « a · b » du texte est comparé aux valeurs affichées des
 * champs de cette ligne (libellé d'option, date courte ou moyenne, nombre, texte). Rien n'est deviné : un segment qui
 * correspond à zéro ou plusieurs champs reste non relié et nommé.
 */
export type QuestionnaireSamples=Record<string,Record<string,unknown>[]>;
export type SampleEntities=Record<string,{fields:ModuleSpecField[]}>;
/** Barème par valeur de liste (QUIZ-NATIVE2 GAP-Q10) exposé sur l'entité qui porte la liste : « +20 XP » d'une question. */
export type SamplePoints={field:string;list:string;scale:Record<string,number>};
export type SegmentMatch={field:string;dateStyle?:TemplateDateStyle;prefix?:string;suffix?:string;optionLabel?:{value:string;label:string}};

export const foldSample=(value:string)=>value.normalize('NFD').replace(/\p{M}/gu,'').toLowerCase().replace(/[’']/g,'\'').replace(/\s+/g,' ').trim();
const SEPARATOR=/ *· */;
const AMOUNT=/^([+]?)([0-9]{1,6})( *[^0-9 ].{0,20})?$/;

/** Formes affichées d'une valeur : libellé d'option (français, puis valeur codée), date courte / moyenne, nombre, texte. */
export function sampleDisplays(field:ModuleSpecField,value:unknown):{text:string;dateStyle?:TemplateDateStyle}[]{
 if(value===null||value===undefined||value==='')return [];
 if(field.data?.type==='static'){
  const option=field.data.options.find(item=>item.value===value);
  return option?[{text:typeof option.label==='string'?option.label:String(option.label?.fr??option.value)}]:[];
 }
 if(['date','datetime'].includes(field.type)&&typeof value==='string'){
  const short=templateDate(value,'fr'),medium=sampleMediumDate(value,'fr'),long=templateDate(value,'fr','long');
  return [...(short?[{text:short}]:[]),...(medium?[{text:medium,dateStyle:'medium' as const}]:[]),...(long?[{text:long,dateStyle:'long' as const}]:[])];
 }
 if(typeof value==='number'||typeof value==='string')return [{text:String(value)}];
 return [];
}
/** Date « 23 oct. 2026 » : même règle que le rendu natif (`dateStyle: 'medium'`), jour inchangé (UTC). */
export function sampleMediumDate(value:string,locale:string):string|null{
 if(!/^\d{4}-\d{2}-\d{2}(?:T[0-9:.]+(?:Z|[+-]\d{2}:\d{2})?)?$/.test(value))return null;
 const date=new Date(value.length===10?value+'T00:00:00Z':value);
 return Number.isFinite(date.getTime())?new Intl.DateTimeFormat(locale,{day:'2-digit',month:'short',year:'numeric',timeZone:'UTC'}).format(date):null;
}

/** La seule ligne dont le champ porte exactement ce texte (plié), sinon rien. */
export function sampleRow(rows:Record<string,unknown>[]|undefined,field:string,text:string):Record<string,unknown>|undefined{
 const wanted=foldSample(text);if(!rows||!wanted)return undefined;
 const found=rows.filter(row=>typeof row[field]==='string'&&foldSample(String(row[field]))===wanted);
 return found.length===1?found[0]:undefined;
}

/** Un segment de capture apparié à exactement un champ (lignes données : la ligne identifiée, ou toutes les lignes d'exemple). */
export function matchSegment(segment:string,fields:ModuleSpecField[],rows:Record<string,unknown>[],points?:SamplePoints):SegmentMatch|undefined{
 const wanted=foldSample(segment);if(!wanted||!rows.length)return undefined;
 const found=new Map<string,SegmentMatch>();
 for(const field of fields){
  if(['id','uuid'].includes(field.name)||field.computed)continue;
  for(const row of rows)for(const display of sampleDisplays(field,row[field.name])){
   if(foldSample(display.text)===wanted){found.set(field.name,{field:field.name,...(display.dateStyle?{dateStyle:display.dateStyle}:{})});continue;}
   // Libellé d'option repris de la capture : la valeur codée figure en fin de segment (« Quiz aléatoire » pour `aleatoire`).
   const value=row[field.name];
   if(field.data?.type==='static'&&typeof value==='string'&&wanted.endsWith(' '+foldSample(value))&&foldSample(display.text)===foldSample(value))found.set(field.name,{field:field.name,optionLabel:{value,label:segment.trim()}});
  }
 }
 const amount=AMOUNT.exec(segment.trim());
 if(points&&amount&&rows.length===1){
  const listed=rows[0][points.list];
  if(typeof listed==='string'&&points.scale[listed]===Number(amount[2]))found.set(points.field,{field:points.field,prefix:amount[1],suffix:amount[3]??''});
 }
 if(found.size>1&&[...found.keys()].every(name=>['date','datetime'].includes(fields.find(field=>field.name===name)?.type??''))){
  // Deux dates du même jour (début et fin d'une partie) : la plus récente de la ligne d'exemple est retenue (mesurée, jamais devinée).
  const row=rows.find(item=>[...found.keys()].every(name=>typeof item[name]==='string'));
  const latest=row?[...found.keys()].sort((a,b)=>String(row[b]).localeCompare(String(row[a]))):[];
  if(latest.length>1&&row![latest[0]]!==row![latest[1]])return found.get(latest[0]);
 }
 return found.size===1?[...found.values()][0]:undefined;
}
/** Tous les segments d'un texte « a · b » : appariés (champ) ou laissés (null) ; rien si aucun segment n'est apparié. */
export function matchSegments(text:string,fields:ModuleSpecField[],rows:Record<string,unknown>[],points?:SamplePoints):{segment:string;match?:SegmentMatch}[]|undefined{
 const segments=text.trim().split(SEPARATOR).filter(Boolean);
 if(!segments.length||segments.length>4)return undefined;
 const result=segments.map(segment=>({segment,match:matchSegment(segment,fields,rows,points)}));
 return result.some(item=>item.match)?result:undefined;
}
